import { ExclamationCircleOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import AdFrame from 'components/UI/AdFrame';
import { hostingStoragePathToUrl } from 'services/Firebase';
import { parseDimensions } from 'stores/DynamicProperties/parse';
import type TearsheetAd from 'stores/TearsheetAd';
import { AdEvents } from 'types/AdEvents';
import { testUrl } from 'utils/Network';

const Container = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
`;

const EmptyPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  flex-flow: column;
  line-height: 1.5;
  color: #999;
`;

const ExclamationCircleIcon = styled(ExclamationCircleOutlined)`
  font-size: 135px;
  margin-bottom: 1rem;
  color: #dddee0;
`;

/**
 * Pulls out the two well-known properties this frame needs.
 *
 * "Well-known" is the backend's term for them - see
 * functions/src/dyndata/wellknown.ts, which holds the canonical list. They are
 * ordinary dynamic properties with the ad's full object model and editing UI
 * behind them, but the build machinery recognises certain names, and only at
 * the root level of the tree. `dimensions` and `template` are two of those.
 *
 * Both come back optional because the source is unvalidated - a tearsheet ad's
 * dynamicData is `unknown` here and `any` on the backend, so nothing has
 * promised these are present or even strings. An ad missing either renders
 * about:blank at 1x1, which is the same outcome as a template whose files have
 * gone: nothing to show, and the placeholder says so.
 */
function extractWellKnownDynamicData(dynamicData: unknown): {
  dimensions: [number, number] | undefined;
  template: string | undefined;
} {
  if (dynamicData && typeof dynamicData === 'object') {
    const { dimensions: uncheckedDimensions, template: uncheckedTemplate } =
      dynamicData as Record<string, unknown>;

    const dimensions =
      typeof uncheckedDimensions === 'string'
        ? parseDimensions(uncheckedDimensions)
        : undefined;

    const template =
      typeof uncheckedTemplate === 'string' ? uncheckedTemplate : undefined;

    return { dimensions, template };
  }
  return { dimensions: undefined, template: undefined };
}

/**
 * Reasons an ad cannot be shown, that can be determined synchronously
 * (during render).
 */
type AdViewProblemSync =
  | 'no-ad'
  | 'no-template'
  | 'no-dimensions'
  | 'bad-template-url';

/**
 * Reasons an ad cannot be shown, that can only be determined
 * asynchrnously.
 *
 * At the time of writing these are exactly equivalent to the non-'ok'
 * members of `utils/Network#UrlProbeResult`.
 */
type AdViewProblemAsync = 'http-error' | 'network-error';

type AdViewProblem = AdViewProblemSync | AdViewProblemAsync;

interface ValidAdViewAttributes {
  valid: true;
  width: number;
  height: number;
  src: string;
}

interface InvalidAdViewAttributes {
  valid: false;
  reason: AdViewProblemSync;
}

type AdViewAttributes = ValidAdViewAttributes | InvalidAdViewAttributes;

/**
 * `sessionToken` is passed in rather than read from the clock, so that the src
 * is stable across renders and the frame reloads only when something decides
 * it should. See the sibling Views/Creative/AdView/AdViewFrame.tsx, which
 * explains the reasoning and why the token keeps a timestamp shape.
 */
function getAdViewAttributes(
  dynamicData: unknown,
  sessionToken: number
): AdViewAttributes {
  const { dimensions, template } = extractWellKnownDynamicData(dynamicData);

  if (!template) return { valid: false, reason: 'no-template' };
  if (!dimensions) return { valid: false, reason: 'no-dimensions' };

  const [width, height] = dimensions;

  try {
    // hostingStoragePathToUrl reaches Firebase's ref(), which validates the
    // path and can reject it - so this is guarded, as the Creative twin's
    // equivalent is. It runs during render, and its input is a property of an
    // `unknown` object that nothing has checked.
    const url = new URL(hostingStoragePathToUrl(template));
    url.searchParams.set('isAdHubPreview', 'true');
    url.searchParams.set('session', `${sessionToken}`);
    return { valid: true, width, height, src: url.href };
  } catch (urlError) {
    console.warn(
      `[Tearsheet AdViewFrame] unusable template path: ${template}`,
      urlError
    );
    return { valid: false, reason: 'bad-template-url' };
  }
}

export interface AdViewPlaceholderProps {
  problem: AdViewProblem;
  adLabel: string | undefined;
}

/**
 * Deliberately descriptive rather than instructive, which is where this parts
 * company with the Creative twin's placeholder. That one tells an author what
 * to do next ("select one in the ad properties"); a tearsheet is a frozen
 * snapshot routinely shown to a reviewer or client, often unauthenticated, who
 * can act on none of it. So these say what is wrong and stop.
 *
 * The exception is 'connection-failed', which is the one thing a viewer can do
 * something about, so it says that it may be worth another look.
 */
function AdViewPlaceholder({ problem, adLabel }: AdViewPlaceholderProps) {
  switch (problem) {
    case 'no-ad':
      return <EmptyPlaceholder>No ad selected.</EmptyPlaceholder>;

    case 'no-template':
      return (
        <EmptyPlaceholder>
          <ExclamationCircleIcon />
          This ad has no template.
        </EmptyPlaceholder>
      );

    case 'no-dimensions':
      return (
        <EmptyPlaceholder>
          <ExclamationCircleIcon />
          This ad has missing or invalid dimensions.
        </EmptyPlaceholder>
      );

    case 'bad-template-url':
      return (
        <EmptyPlaceholder>
          <ExclamationCircleIcon />
          This ad’s template cannot be located.
        </EmptyPlaceholder>
      );

    case 'http-error':
      return (
        <EmptyPlaceholder>
          <ExclamationCircleIcon />
          <b>{adLabel}</b> is unavailable.
        </EmptyPlaceholder>
      );

    case 'network-error':
      return (
        <EmptyPlaceholder>
          <ExclamationCircleIcon />
          Could not load <b>{adLabel}</b>. This may be a temporary connection
          issue.
        </EmptyPlaceholder>
      );

    default: {
      // Annotating :never means tsc reports an error if the cases above stop
      // being exhaustive.
      const unhandledProblem: never = problem;
      console.warn(
        '[AdViewFrame#AdViewPlaceholder] Unhandled problem',
        unhandledProblem
      );
      return null;
    }
  }
}

export interface AdViewFrameProps {
  ad?: TearsheetAd | null | undefined;
  viewScale?: number | null | undefined;
}

function AdViewFrame({ ad, viewScale }: AdViewFrameProps) {
  const [sessionToken, setSessionToken] = useState(() => Date.now());
  const requestReload = useCallback(
    () => setSessionToken((previous) => Math.max(Date.now(), previous + 1)),
    []
  );

  const attributes: AdViewAttributes = ad
    ? getAdViewAttributes(ad.data.dynamicData, sessionToken)
    : { valid: false, reason: 'no-ad' };

  // Only defined when there is something to load, which is what keys the
  // reachability check below.
  const src = attributes.valid ? attributes.src : undefined;

  const adLabel = ad?.label;

  // Reload on an ad switch. No mobx reaction needed: TearsheetAdsManager
  // .current is `children.find(ad => ad.active)` over observable fields, so
  // reading it during this observer's render already re-renders on a switch -
  // the reaction only ever needed to trigger the reload, which this does.
  //
  // A second reaction watched store.router.params.adIndex. That was redundant:
  // setActiveByIndex is only ever called from Routes.tsx in response to that
  // same param, so the param is the sole cause of `current` changing. The one
  // case it caught alone was navigating to the ad already showing, where a
  // reload is not wanted.
  //
  // Skipped on mount: the first render already loads the frame.
  const previouslyLoadedAd = useRef(ad);
  useEffect(() => {
    if (previouslyLoadedAd.current === ad) return;
    previouslyLoadedAd.current = ad;
    requestReload();
  }, [ad, requestReload]);

  // The toolbar's ReloadButton calls TearsheetAd.reload(), which dispatches
  // this and nothing else listens for it.
  useEffect(() => {
    ad?.addEventListener(AdEvents.RELOAD, requestReload);
    return () => {
      ad?.removeEventListener(AdEvents.RELOAD, requestReload);
    };
  }, [ad, requestReload]);

  // No AdEvents.CHANGED listener and no debounce, unlike the Creative frame: a
  // tearsheet ad is a snapshot of the properties an ad had when it was taken,
  // so there is nothing to keep in step with. Answering the frame's request
  // for that snapshot is AdFrame's job.
  //
  // An ad's template can point at storage that will not serve it. This used
  // to raise a toast and hide the frame, which left nothing behind: a toast
  // is gone in seconds, and can be missed entirely if it resolves while the
  // tab is in the background. The frame then just sat there blank. It is a
  // placeholder now, which is legible whenever anyone looks.
  //
  // Keyed on `src`, so it re-checks on every reload, and aborted on cleanup.
  // The class version had no such guard, and its comment recorded the
  // consequence: two quick ad switches put two checks in flight, and a slow
  // first one could hide the second ad's frame.
  const [templateProblem, setTemplateProblem] = useState<AdViewProblemAsync>();

  useEffect(() => {
    setTemplateProblem(undefined);
    if (!src) return;

    const abortCheck = new AbortController();

    void testUrl(src, abortCheck.signal).then((result) => {
      // An abort reports 'network-error', so it has to be discarded before
      // the result is read.
      if (abortCheck.signal.aborted || result === 'ok') return;
      setTemplateProblem(result);
    });

    return () => abortCheck.abort();
  }, [src]);

  // Two early returns rather than one: each narrows its own discriminant, and
  // together they cover everything getAdViewAttributes could decide during
  // render plus the two things only the network could answer.
  //
  // The `!ad` arm is unreachable - without an ad, attributes is already
  // 'no-ad' - but it is what tells tsc that ad is defined further down.
  if (!attributes.valid || !ad) {
    return (
      <Container>
        <AdViewPlaceholder
          problem={attributes.valid ? 'no-ad' : attributes.reason}
          adLabel={adLabel}
        />
      </Container>
    );
  }

  if (templateProblem) {
    return (
      <Container>
        <AdViewPlaceholder problem={templateProblem} adLabel={adLabel} />
      </Container>
    );
  }

  return (
    <Container>
      <AdFrame
        src={attributes.src}
        width={attributes.width}
        height={attributes.height}
        scale={viewScale ?? 1}
        iframeName="adframe"
        getDynamicData={() => ad.previewData()}
      />
    </Container>
  );
}

// Still a MobX observer, despite taking everything through props: `ad`
// is a DocumentStore, and reading `ad.data.dynamicData` and `ad.label`
// during render is reading observables.
export default observer(AdViewFrame);
